import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, TextField, Button, Stack, Switch, FormControlLabel, FormControl, InputLabel, Select, MenuItem, CircularProgress, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CategoryIcon from '@mui/icons-material/Category';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { getBlogDetail, createBlog, updateBlog } from '../../api/endpoints/blog';
import { getCategories } from '../../api/endpoints/categories';
import { uploadFile } from '../../api/endpoints/media';
import CategoryDialog from './CategoryDialog';
import RichTextEditor from '../../components/RichTextEditor';

const BlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [form, setForm] = useState({
    title: '', desc: '', content: '', image: '', category: '', isActive: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    fetchCategories();
    if (isEdit) fetchBlog();
  }, [id]);

  const fetchCategories = async () => {
    try {
      const res = await getCategories({ type: 'blog' });
      setCategories(res.data.data || []);
    } catch { /* ignore */ }
  };

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const res = await getBlogDetail(id);
      const blog = res.data.data;
      setForm({
        title: blog.title || '',
        desc: blog.desc || '',
        content: blog.content || '',
        image: blog.image || '',
        category: blog.category?._id || blog.category || '',
        isActive: blog.isActive ?? true,
      });
    } catch { toast.error('Failed to load blog'); }
    finally { setLoading(false); }
  };

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(true);
    try {
      const res = await uploadFile(file);
      const url = res.data.data.url;
      setForm((prev) => ({ ...prev, image: url }));
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image uploaded');
    } catch {
      toast.error('Image upload failed');
    } finally {
      setImageUploading(false);
    }
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setForm((prev) => ({ ...prev, image: '' }));
    setImagePreview(null);
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.category) delete payload.category;
      if (isEdit) {
        await updateBlog(id, payload);
        toast.success('Blog updated');
      } else {
        await createBlog(payload);
        toast.success('Blog created');
      }
      navigate('/blogs');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/blogs')} sx={{ mb: 2 }}>Back</Button>
      <Typography variant="h5" fontWeight={700} gutterBottom>{isEdit ? 'Edit Blog' : 'Add Blog'}</Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card><CardContent>
            <Stack spacing={2}>
              <TextField label="Title" value={form.title} onChange={handleChange('title')} fullWidth required />
              <TextField label="Short Description" value={form.desc} onChange={handleChange('desc')} fullWidth multiline rows={2} />
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((prev) => ({ ...prev, content: html }))}
              />
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select value={form.category} label="Category" onChange={handleChange('category')}>
                      {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <Button variant="outlined" startIcon={<CategoryIcon />} onClick={() => setCatDialogOpen(true)} fullWidth>
                    Manage Categories
                  </Button>
                </Grid>
              </Grid>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />} disabled={imageUploading}>
                  {imageUploading ? 'Uploading...' : 'Upload Image'}
                  <input type="file" hidden accept="image/*" onChange={handleUploadImage} />
                </Button>
                <TextField label="Image URL" value={form.image} onChange={handleChange('image')} fullWidth size="small" placeholder="https://example.com/image.jpg" />
              </Stack>
              {(imagePreview || form.image) && (
                <Box sx={{ position: 'relative', display: 'inline-block', mt: 1 }}>
                  <img src={imagePreview || form.image} alt="Preview"
                    style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, objectFit: 'cover' }}
                    onError={(e) => { if (!imagePreview) e.target.style.display = 'none'; }}
                  />
                  <IconButton size="small" sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white' }} onClick={handleRemoveImage}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />} label="Active" />
            </Stack>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button variant="contained" size="large" fullWidth onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update Blog' : 'Create Blog')}
          </Button>
        </Grid>
      </Grid>
      <CategoryDialog open={catDialogOpen} onClose={() => setCatDialogOpen(false)} type="blog" onSaved={fetchCategories} />
    </Box>
  );
};

export default BlogForm;
